import {
  Badge,
  DateText,
  EmptyState,
  Menu,
  type MenuItem,
  Skeleton,
  Tabs,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { splitCover, stripMarkdown } from '@/lib/markdown';
import { A, useNavigate } from '@solidjs/router';
import { For, Show, createMemo, createSignal } from 'solid-js';
import { type PollListItem, copyPollLink } from './api';
import { useClosePoll, useDeletePoll, usePolls } from './hooks';

const STATUS_TONE = { draft: 'neutral', open: 'success', closed: 'neutral' } as const;
const GRADIENTS = [
  'from-brand-600 to-violet-600',
  'from-cyan-600 to-brand-600',
  'from-pink-600 to-orange-500',
  'from-emerald-600 to-cyan-600',
];
const DUE_SOON_MS = 2 * 24 * 60 * 60 * 1000;

const initials = (title: string) =>
  title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

const PollCard = (props: { poll: PollListItem }) => {
  const parts = createMemo(() => splitCover(props.poll.description));
  const [broken, setBroken] = createSignal(false);
  const dueSoon = () =>
    props.poll.status === 'open' &&
    props.poll.closesAt !== null &&
    new Date(props.poll.closesAt).getTime() - Date.now() < DUE_SOON_MS;
  const gradient = () =>
    GRADIENTS[
      [...props.poll.id].reduce((sum, char) => sum + char.charCodeAt(0), 0) % GRADIENTS.length
    ];
  return (
    <article class="group relative flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg motion-reduce:transition-none motion-reduce:hover:translate-y-0 dark:border-neutral-800 dark:bg-surface-dark">
      <div class="relative aspect-[16/7] overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        <Show
          when={parts().cover && !broken()}
          fallback={
            <div
              class={`flex h-full items-center justify-center bg-gradient-to-br ${gradient()}`}
              aria-hidden="true"
            >
              <span class="text-3xl font-bold tracking-wide text-white/90">
                {initials(props.poll.title)}
              </span>
            </div>
          }
        >
          <img
            src={parts().cover ?? undefined}
            alt=""
            loading="lazy"
            onError={() => setBroken(true)}
            class="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
          />
        </Show>
        <div class="absolute left-3 top-3 flex flex-wrap gap-1.5">
          <Badge tone={STATUS_TONE[props.poll.status]} class="shadow-sm">
            {props.poll.status}
          </Badge>
          <Show when={props.poll.isAnonymous}>
            <Badge class="shadow-sm">anonymous</Badge>
          </Show>
        </div>
      </div>
      <div class="flex flex-1 flex-col gap-1.5 p-4">
        <A
          href={`/polls/${props.poll.id}`}
          class="font-semibold leading-snug text-neutral-900 after:absolute after:inset-0 hover:text-brand-600 dark:text-neutral-100 dark:hover:text-brand-400"
        >
          {props.poll.title}
        </A>
        <Show when={stripMarkdown(parts().body)}>
          {(text) => <p class="line-clamp-2 text-sm text-neutral-500">{text()}</p>}
        </Show>
        <div class="mt-auto flex flex-wrap items-center justify-between gap-2 pt-3 text-xs text-neutral-500">
          <span>
            <Show when={props.poll.hasResponded}>
              <span class="font-semibold text-emerald-600 dark:text-emerald-400">
                ✓ You responded
              </span>
              {' · '}
            </Show>
            {props.poll.responseCount} response{props.poll.responseCount === 1 ? '' : 's'}
          </span>
          <span class={dueSoon() ? 'font-semibold text-orange-600 dark:text-orange-400' : ''}>
            <Show when={props.poll.closesAt} fallback="No deadline">
              {(at) => (
                <>
                  Closes <DateText value={at()} />
                </>
              )}
            </Show>
          </span>
        </div>
      </div>
      <div class="absolute right-2 top-2 z-10 rounded-lg bg-white/90 shadow-sm backdrop-blur dark:bg-neutral-900/80">
        <PollMenu poll={props.poll} />
      </div>
    </article>
  );
};

export const PollsList = () => {
  const query = usePolls();
  const [tab, setTab] = createSignal('open');
  const rows = createMemo(() => {
    const all = query.data?.data ?? [];
    return all.filter((poll) =>
      tab() === 'closed' ? poll.status === 'closed' : poll.status !== 'closed',
    );
  });
  const counts = createMemo(() => {
    const all = query.data?.data ?? [];
    return {
      open: all.filter((poll) => poll.status !== 'closed').length,
      closed: all.filter((poll) => poll.status === 'closed').length,
    };
  });

  return (
    <>
      <Tabs
        tabs={[
          { id: 'open', label: 'Open', count: counts().open },
          { id: 'closed', label: 'Closed', count: counts().closed },
        ]}
        active={tab()}
        onChange={setTab}
        class="mb-5"
      />
      <Show when={!query.isPending} fallback={<Skeleton class="h-48" />}>
        <Show
          when={!query.isError}
          fallback={<EmptyState variant="error" title="Failed to load polls" />}
        >
          <Show
            when={rows().length > 0}
            fallback={
              <EmptyState
                title={tab() === 'closed' ? 'No closed polls' : 'No open polls'}
                body="Create a survey to collect responses from the workspace."
              />
            }
          >
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <For each={rows()}>{(poll) => <PollCard poll={poll} />}</For>
            </div>
          </Show>
        </Show>
      </Show>
    </>
  );
};

const PollMenu = (props: { poll: PollListItem }) => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const close = useClosePoll(props.poll.id);
  const remove = useDeletePoll(props.poll.id);
  const confirm = useConfirm();
  const toast = useToast();
  const items = (): MenuItem[] => {
    const result: MenuItem[] = [
      { label: 'Open', onSelect: () => navigate(`/polls/${props.poll.id}`) },
      {
        label: 'Copy link',
        onSelect: () =>
          copyPollLink(props.poll.slug).then(
            () => toast.success('Link copied'),
            () => toast.error('Could not copy link'),
          ),
      },
    ];
    if (props.poll.canViewResults) {
      result.push({
        label: 'Results',
        onSelect: () => navigate(`/polls/${props.poll.id}/results`),
      });
    }
    const canDeleteAny = session()?.permissions.has('polls:delete') ?? false;
    const canClose =
      (session()?.permissions.has('polls:close') ?? false) &&
      (props.poll.canManage || canDeleteAny);
    if (props.poll.status === 'open' && canClose) {
      result.push({
        label: 'Close now',
        onSelect: async () => {
          if (!(await confirm({ title: `Close ${props.poll.title}?`, confirmLabel: 'Close' })))
            return;
          await close.mutateAsync(undefined as never);
          toast.success('Poll closed');
        },
      });
    }
    if (props.poll.canManage || canDeleteAny) {
      result.push({
        label: 'Delete',
        danger: true,
        onSelect: async () => {
          if (
            !(await confirm({
              title: `Delete ${props.poll.title}?`,
              body: 'This permanently deletes every response.',
              confirmLabel: 'Delete',
              danger: true,
            }))
          )
            return;
          await remove.mutateAsync(undefined as never);
          toast.success('Poll deleted');
        },
      });
    }
    return result;
  };
  return <Menu items={items()} />;
};
